import type { StaticEntityType } from "@config";
import { cn } from "@lib/cn";
import { Set } from "@library-containers/layers";
import { useMemo } from "react";
import { CarouselCtxProvider } from "../ctx";
import {
  CarouselContainerEnum,
  CarouselStyleEnum,
  type ICarouselItemBgImageData,
  type ICarouselSettings,
} from "../types";
import { ensureCarouselComboSettings } from "../utils";
import { CarouselBgImage } from "./carousel-bg/carousel-bg-image";
import { CarouselItemWrapper } from "./carousel-items/carousel-item-wrapper";
import { CarouselPagination } from "./carousel-pagination";

interface IProps {
  entity: StaticEntityType;
}

export const CarouselPdf = ({ entity }: IProps) => {
  const { children: sets } = entity;

  const comboSettings = useMemo(() => {
    return ensureCarouselComboSettings({
      comboSettings: entity.settings,
    });
  }, [entity.settings]);

  const isSideBySide =
    comboSettings.carouselContainer === CarouselContainerEnum.SideBySide;
  const isFullScreen =
    comboSettings.carouselStyle === CarouselStyleEnum.FullScreen;

  return (
    <CarouselCtxProvider
      ctxState={{
        comboId: entity.id,
        comboLayers: entity.layers,
        sets,
        handleSwapNext: () => {},
        handleSwapPrev: () => {},
        isFullScreen,
        isSideBySide,
        comboSettings: entity.settings as ICarouselSettings,
        isEditor: false,
      }}
    >
      <div
        className="carousel-combo tw-relative tw-flex tw-w-full"
        data-carousel-container={comboSettings.carouselContainer}
      >
        <div className="tw-flex tw-w-full tw-flex-col tw-items-center">
          {sets.map((set, idx) => (
            <div
              key={set.id}
              className={cn({
                "tw-relative tw-w-full tw-break-inside-avoid tw-px-12": true,
                "tw-flex tw-min-h-[595px] tw-items-center": isFullScreen,
                "tw-mt-16 first-of-type:tw-mt-0": !isFullScreen,
              })}
            >
              <CarouselItemWrapper set={set}>
                <Set
                  entity={{
                    ...(set as StaticEntityType),
                    layers: {
                      ...(entity.layers || {}),
                      combo: {
                        settings: comboSettings,
                        index: idx,
                        id: entity.id,
                      },
                    },
                  }}
                />
              </CarouselItemWrapper>
              {isFullScreen && (
                <CarouselBgImage
                  isVisible
                  data={set.settings as ICarouselItemBgImageData}
                />
              )}
              <div
                className={cn({
                  "tw-relative tw-flex tw-justify-center": true,
                  "tw-absolute tw-bottom-4 tw-left-1/2 -tw-translate-x-1/2":
                    isFullScreen,
                  "tw-mt-8": !isFullScreen,
                })}
              >
                <CarouselPagination activeId={set.id} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </CarouselCtxProvider>
  );
};
